// HU-09 y HU-46 · El expediente del caso y sus documentos. F-02, Sprint 1.
//
// El expediente nace con el numero: RN-01 dice que un borrador no tiene ni uno ni
// otro, y ambos se crean en la MISMA transaccion que toma el correlativo (RN-07).
// Por eso esta pantalla solo tiene sentido despues de enviar.

import { pedir, pedirBinario, segUuid } from './cliente';
import type { RespuestaDelApi } from '../tipos';

/**
 * HU-09. Devuelve el expediente con sus documentos y lo que le falta.
 *
 * `faltantes` y `esta_completo` los calcula el servidor: es el mismo criterio que
 * decide si el expediente puede archivarse, y duplicarlo en el front garantizaria
 * que un dia digan cosas distintas.
 */
export function verExpediente(casoId: string) {
  return pedir<RespuestaDelApi>(`/expedientes/${segUuid(casoId)}`);
}

/**
 * HU-46. Archiva un documento ya generado (el Formato Verde, por ejemplo).
 *
 * ADR-003: el PDF es la fuente de verdad de lo declarado, y por eso
 * `documento_version` no admite UPDATE ni DELETE. Archivar es SIEMPRE una version
 * nueva, nunca una correccion de la anterior.
 */
type DocumentoAArchivar = {
  tipoDeDocumento: string;
  rutaBlob: string;
  huellaSha256: string;
  contienePii?: boolean;
  procedencia?: string;
};

export function archivarDocumento(casoId: string, { tipoDeDocumento, rutaBlob, huellaSha256, contienePii, procedencia }: DocumentoAArchivar) {
  return pedir(`/expedientes/${segUuid(casoId)}/documentos`, {
    metodo: 'POST',
    cuerpo: {
      tipo_de_documento: tipoDeDocumento,
      ruta_blob: rutaBlob,
      huella_sha256: huellaSha256,
      contiene_pii: contienePii,
      ...(procedencia ? { procedencia } : {}),
    },
  });
}

/**
 * T-38 · Los BYTES del PDF de un documento del expediente, como `Blob`.
 *
 * El servidor responde `application/pdf` directamente —no una URL firmada—: asi el
 * visor no depende de que el navegador pueda alcanzar el almacenamiento, y el
 * backend verifica la huella (ADR-003) ANTES de entregar; si no cuadra responde
 * 500 con codigo `custodia_rota`, que es irrecuperable y no debe reintentarse.
 *
 * Un documento con `contiene_pii` es dato L2 (RN-09): se sirve a quien tiene concedido
 * el permiso y su lectura deja asiento; a quien no lo tiene le responde 403 con
 * `codigo: "acceso_denegado"`, y ese intento TAMBIEN queda registrado (HU-33, AF §13).
 * Hasta el 22-sep-2026 respondia 403 a todo el mundo, tuviera permiso o no.
 */
export function contenidoDeDocumento(casoId: string, documentoId: string) {
  return pedirBinario(`/expedientes/${segUuid(casoId)}/documentos/${segUuid(documentoId)}/contenido`);
}

/**
 * HU-07 / HU-49-C9 · Los BYTES de una evidencia ADJUNTA al caso, como `Blob`.
 *
 * Hermano de `contenidoDeDocumento` y endpoint distinto a proposito: un documento es
 * una version que el sistema produjo; una evidencia es un archivo que una persona
 * subio. Comparten la guarda L2 y la verificacion de huella, no la identidad.
 *
 * Antes del 22-sep-2026 NO habia forma de abrir una evidencia: el expediente las
 * listaba —nombre, origen, quien la subio— y no existia ruta que las sirviera, porque
 * el binario sube directo al almacenamiento con una URL firmada de ESCRITURA (HU-07)
 * y nadie emitia nunca la de lectura.
 */
export function contenidoDeEvidencia(casoId: string, adjuntoId: string) {
  return pedirBinario(`/expedientes/${segUuid(casoId)}/evidencias/${segUuid(adjuntoId)}/contenido`);
}

// ---- HU-26 · HU-48 · HU-52 (AT S3 §2.2.4) · las filas del expediente y el reintento -------

/** Una versión archivada dentro de su fila: «Informe FAR-03 · v2 · 07/10/2026 · Vigente». */
export type VersionDeLaFila = {
  documento_id: string;
  version: number;
  /** El texto de la versión, armado por el servidor (fecha dd/mm/aaaa y «Vigente» incluidos). */
  texto: string;
  huella_sha256: string;
  es_la_vigente: boolean;
  /** `descargar`, ya filtrado por quien consulta. */
  acciones_permitidas: string[];
};

/**
 * Una fila por documento que le toca al caso según su gravedad (HU-26-C3, HU-52-C5). El
 * servidor decide el `estado` (`generado` · `pendiente` · `fallido` · `hay_que_aportarlo`) y
 * su `texto_del_estado`; la pantalla los pinta tal cual (§8.1 R1).
 */
export type FilaDelExpediente = {
  tipo: string;
  etiqueta: string;
  estado: string;
  texto_del_estado: string;
  momento_de_aparicion: string | null;
  /** La versión del encargo pendiente o fallido; con `tipo`, la dirección del reintento. Nula en el faltante sin encargo. */
  version_del_encargo: number | null;
  motivo_fallo: string | null;
  /** Solo la carta (HU-52-C3): el día en que se presentó. */
  enviada_en: string | null;
  /** `descargar` y/o `reintentar`, ya filtradas por quien consulta (P-03). */
  acciones_permitidas: string[];
  /** Hay una versión con dato de personas y quien consulta no tiene el permiso L2. */
  reservado_l2: boolean;
  /** «Documento reservado · requiere permiso L2». */
  texto_reservado: string | null;
  versiones: VersionDeLaFila[];
  /**
   * CC-02 (B9, RN-80, T-7): la carta a la que pertenece la fila (Formato Amarillo, carta y cargo);
   * nula en los documentos del caso. Con varias cartas, la etiqueta ya nombra la autoridad. Es la
   * dirección del reintento (`?autoridad=`). Opcional: un servidor anterior no la trae.
   */
  autoridad_codigo?: string | null;
};

/**
 * HU-48-C8 · HU-52-C7 · reintentar la generación de un documento.
 *
 * El encargo no tiene id propio: se direcciona por `tipo` y `version_del_encargo` de la fila
 * (decisión del operador del 30-sep). Responde 202 SIN cuerpo; quien llama refresca el
 * expediente (§8.1 R3). Un 409 dice que el encargo ya está generado o en curso.
 */
export function reintentarEncargo(casoId: string, tipo: string, version: number, autoridad?: string | null) {
  if (!/^[a-z0-9_]+$/.test(tipo) || !Number.isInteger(version) || version < 1
    || (autoridad != null && !/^[A-Z0-9_]{1,40}$/.test(autoridad))) {
    throw new Error(`Encargo no válido para una ruta: ${JSON.stringify({ tipo, version, autoridad })}`);
  }
  // CC-02 (B9): la copia de una carta concreta se direcciona también por su autoridad.
  const deLaCarta = autoridad ? `?autoridad=${autoridad}` : '';
  return pedir<null>(`/expedientes/${segUuid(casoId)}/encargos/${tipo}/${version}/reintento${deLaCarta}`, { metodo: 'POST' });
}

// ---- ui/MG-ILE-54, -53, -56 y -69 · `caso.*`, `disposiciones[]` y `acciones_permitidas` ------

/**
 * Una fila de `caso.cierres[]` (`PasoDelCierreSalida`): «Cierre 1 · …», «Reapertura 1 · … ·
 * {motivo}». La gravedad anterior y la nueva, solo en la reapertura que reclasificó.
 */
export type PasoDelCierre = {
  /** `cierre` o `reapertura`. */
  accion: string;
  orden: number;
  quien: string;
  cuando: string;
  motivo: string | null;
  gravedad_anterior: string | null;
  gravedad_nueva: string | null;
};

/**
 * `caso.*` del expediente (`CasoDelExpedienteSalida`), ya resuelto para quien consulta: el
 * estado y la gravedad con su texto (§8.1 R1) y lo que esta sesión puede hacer con el caso.
 */
export type CasoDelExpediente = {
  estado_del_caso: string;
  /** Figura 11 del AF S3: «Cerrado», «En seguimiento». */
  texto_del_estado: string;
  gravedad: string | null;
  /** «Grave», «No grave» o «Sin decidir». */
  texto_de_la_gravedad: string;
  /** La de antes de la última reclasificación; nula si nunca cambió (HU-28-C7). */
  gravedad_anterior: string | null;
  /** Del cierre vigente; nulos si el caso no está cerrado (HU-27-C7). */
  cerrado_por: string | null;
  cerrado_en: string | null;
  /** «Reabierto 1 vez» (HU-28-C1); nulo si nunca se reabrió. */
  texto_de_reaperturas: string | null;
  cierres: PasoDelCierre[];
  /** `reabrir` (Comité, caso cerrado) y `registrar_disposicion` (AR o Rep. País de su ámbito, grave no cerrado). */
  acciones_permitidas: string[];
  /** Las gravedades que la reapertura aceptaría (HU-28-C7, C9); vacía si quien consulta no puede reabrir. */
  reclasificaciones_permitidas: string[];
  /** El aviso de HU-28-C8 si se reclasifica a grave; nulo si no aplica. */
  texto_del_plazo_si_grave: string | null;
};

/**
 * Una fila de `expediente.disposiciones[]` (`DisposicionDelExpedienteSalida`, ui/MG-ILE-53):
 * los mismos nombres y textos que la fila de `GET /regulatorio/compromisos` (§8.1 R8).
 */
export type DisposicionDelExpediente = {
  disposicion_id: string;
  orden: number;
  /** `disposicion` o `solicitud_de_informacion`. */
  tipo: string;
  texto_de_clase: string;
  descripcion: string;
  autoridad: string | null;
  responsable: string;
  /** aaaa-mm-dd */
  plazo: string;
  estado: string;
  texto_del_estado: string;
  esta_vencido: boolean;
  /** «Vencido hace 9 días»; nulo si no venció. */
  texto_de_vencimiento: string | null;
  /** «Registrada por Ana Ríos». */
  texto_de_registro: string | null;
  cumplido_en: string | null;
  /** CC-02 (B9, RN-78): el código de la autoridad que la emitió (`autoridad` es su nombre). */
  autoridad_codigo?: string | null;
};

/** Las acciones que el servidor puede ofrecer sobre el caso y el expediente. */
export const ACCION_REABRIR = 'reabrir';
export const ACCION_REGISTRAR_DISPOSICION = 'registrar_disposicion';
export const ACCION_VER_TRAZABILIDAD = 'ver_trazabilidad';
/** Bug #1098 (ajuste de cierre): la tarjeta «Avisos enviados»; el Especialista invitado no la recibe. */
export const ACCION_VER_AVISOS = 'ver_avisos';
